import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Switch } from '../components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, CalendarDays, Clock, MapPin, Pill, Stethoscope, AlertCircle, Trash2, Download, RefreshCw, Pencil } from 'lucide-react';

import AppLogo from '../components/AppLogo';

const CalendarPageStandalone = () => {
  const { user } = useContext(AuthContext);
  const [events, setEvents] = useState([]);
  const [circles, setCircles] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
  const [gcalConnected, setGcalConnected] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [selectedCircleId, setSelectedCircleId] = useState('');
  const [newEvent, setNewEvent] = useState({
    title: '', description: '', event_type: 'appointment',
    start_time: '', end_time: '', location: '', attendees: [], all_day: false, recurrence: null
  });
  const [editEvent, setEditEvent] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const toLocalInput = (s) => (s ? String(s).slice(0, 16) : '');

  const openEdit = (ev) => {
    setEditEvent({
      ...ev,
      start_time: toLocalInput(ev.start_time),
      end_time: toLocalInput(ev.end_time),
    });
    setEditDialogOpen(true);
  };

  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/calendar-events/${editEvent.id}`, {
        title: editEvent.title,
        description: editEvent.description,
        event_type: editEvent.event_type,
        start_time: editEvent.start_time,
        end_time: editEvent.end_time,
        location: editEvent.location,
        all_day: editEvent.all_day,
      });
      toast.success('Event updated');
      setEditDialogOpen(false);
      setEditEvent(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not update event'); }
  };

  const eventTypes = [
    { value: 'appointment', label: 'Appointment', icon: <Stethoscope className="w-4 h-4" />, color: 'bg-sky-500', bg: 'bg-sky-50 text-sky-700' },
    { value: 'medication', label: 'Medication', icon: <Pill className="w-4 h-4" />, color: 'bg-cyan-500', bg: 'bg-cyan-50 text-cyan-600' },
    { value: 'task', label: 'Task', icon: <CalendarDays className="w-4 h-4" />, color: 'bg-violet-500', bg: 'bg-violet-50 text-violet-700' },
    { value: 'reminder', label: 'Reminder', icon: <AlertCircle className="w-4 h-4" />, color: 'bg-amber-500', bg: 'bg-amber-50 text-amber-700' },
    { value: 'other', label: 'Other', icon: <CalendarDays className="w-4 h-4" />, color: 'bg-slate-500', bg: 'bg-slate-50 text-slate-700' }
  ];

  const fmtCal = (d) => d ? new Date(d).toISOString().replace(/-|:|\.\d+/g, '') : '';
  const getGoogleUrl = (ev) => {
    const s = fmtCal(ev.start_time), e = ev.end_time ? fmtCal(ev.end_time) : fmtCal(new Date(new Date(ev.start_time).getTime()+3600000));
    return `https://calendar.google.com/calendar/render?${new URLSearchParams({action:'TEMPLATE',text:ev.title,dates:`${s}/${e}`,details:ev.description||'',location:ev.location||''})}`;
  };
  const getOutlookUrl = (ev) => {
    return `https://outlook.live.com/calendar/0/deeplink/compose?${new URLSearchParams({path:'/calendar/action/compose',rru:'addevent',subject:ev.title,startdt:new Date(ev.start_time).toISOString(),enddt:ev.end_time?new Date(ev.end_time).toISOString():new Date(new Date(ev.start_time).getTime()+3600000).toISOString(),body:ev.description||'',location:ev.location||''})}`;
  };
  const downloadICS = (ev) => {
    const s = fmtCal(ev.start_time), e = ev.end_time ? fmtCal(ev.end_time) : fmtCal(new Date(new Date(ev.start_time).getTime()+3600000));
    const ics = ['BEGIN:VCALENDAR','VERSION:2.0','BEGIN:VEVENT',`DTSTART:${s}`,`DTEND:${e}`,`SUMMARY:${ev.title}`,`DESCRIPTION:${ev.description||''}`,`LOCATION:${ev.location||''}`,'END:VEVENT','END:VCALENDAR'].join('\r\n');
    const blob = new Blob([ics],{type:'text/calendar;charset=utf-8'});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=`${ev.title.replace(/[^a-z0-9]/gi,'_')}.ics`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
  };

  const fetchData = useCallback(async () => {
    try {
      const [eventsRes, circlesRes, gcalRes] = await Promise.all([
        axios.get(`${API}/my-events`),
        axios.get(`${API}/circles`),
        axios.get(`${API}/gcal/status`).catch(() => ({ data: { connected: false } }))
      ]);
      setEvents(eventsRes.data);
      setCircles(circlesRes.data);
      setGcalConnected(gcalRes.data.connected);
      if (circlesRes.data.length > 0) setSelectedCircleId(circlesRes.data[0].id);
    } catch (error) { toast.error('Could not load calendar'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGcalSync = async () => {
    setSyncLoading(true);
    try {
      const res = await axios.post(`${API}/gcal/sync`, { circle_id: selectedCircleId || undefined });
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally { setSyncLoading(false); }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!selectedCircleId) { toast.error('Pick a circle first'); return; }
    try {
      await axios.post(`${API}/calendar-events`, { ...newEvent, circle_id: selectedCircleId });
      toast.success('Event added to calendar');
      setAddEventDialogOpen(false);
      setNewEvent({ title:'', description:'', event_type:'appointment', start_time:'', end_time:'', location:'', attendees:[], all_day:false, recurrence:null });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Could not add event'); }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Remove this event?')) return;
    try {
      await axios.delete(`${API}/calendar-events/${eventId}`);
      toast.success('Event removed');
      fetchData();
    } catch (error) { toast.error('Could not remove'); }
  };

  const getTypeInfo = (type) => eventTypes.find(t => t.value === type) || eventTypes[4];
  const getEventsForDate = (date) => events.filter(ev => {
    const d = new Date(ev.start_time);
    return d.getDate()===date.getDate() && d.getMonth()===date.getMonth() && d.getFullYear()===date.getFullYear();
  });
  const selectedDayEvents = getEventsForDate(selectedDate);
  const eventDates = events.map(ev => new Date(ev.start_time));
  const calendarModifiers = useMemo(() => ({ hasEvent: eventDates }), [eventDates]);
  const calendarModifiersClassNames = useMemo(() => ({ hasEvent: 'bg-cyan-100 text-cyan-800 font-bold' }), []);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-200 border-t-cyan-600"></div></div>;
  }

  return (
    <div className="min-h-screen pb-24" data-testid="calendar-page">
      <header className="surface-header sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AppLogo />
            <div>
              <h1 className="text-xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Calendar</h1>
              <p className="text-xs text-muted-foreground">Appointments, medications & important dates</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {gcalConnected && (
              <Button variant="outline" size="sm" className="h-9 text-sm" onClick={handleGcalSync} disabled={syncLoading} data-testid="gcal-sync-calendar-btn">
                <RefreshCw className={`w-4 h-4 mr-1.5 ${syncLoading ? 'animate-spin' : ''}`} /> Sync Google
              </Button>
            )}
          <Dialog open={addEventDialogOpen} onOpenChange={setAddEventDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary h-9 text-sm" data-testid="add-event-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New Event
              </Button>
            </DialogTrigger>
            <DialogContent className="surface-card border-0 shadow-xl max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add Event</DialogTitle>
                <DialogDescription>Schedule something for your circle</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddEvent} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Circle</Label>
                    <Select value={selectedCircleId} onValueChange={setSelectedCircleId}>
                      <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select circle" /></SelectTrigger>
                      <SelectContent>{circles.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={newEvent.event_type} onValueChange={(v) => setNewEvent({ ...newEvent, event_type: v })}>
                      <SelectTrigger data-testid="event-type-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>{eventTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input data-testid="event-title-input" placeholder="e.g., Doctor visit" value={newEvent.title} onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })} className="rounded-xl" required />
                </div>
                <div className="space-y-2">
                  <Label>Details (optional)</Label>
                  <Textarea data-testid="description-input" placeholder="Notes..." value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} className="resize-none rounded-xl" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Start</Label><Input type="datetime-local" data-testid="start-time-input" value={newEvent.start_time} onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })} className="rounded-xl" required /></div>
                  <div className="space-y-2"><Label>End</Label><Input type="datetime-local" data-testid="end-time-input" value={newEvent.end_time} onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })} className="rounded-xl" /></div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={newEvent.all_day} onCheckedChange={(c) => setNewEvent({ ...newEvent, all_day: c })} data-testid="all-day-switch" />
                  <Label>All day</Label>
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input data-testid="location-input" placeholder="Address or link" value={newEvent.location} onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })} className="rounded-xl" />
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="submit-event-btn">Add to Calendar</Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="surface-card p-4">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="rounded-xl"
                modifiers={calendarModifiers}
                modifiersClassNames={calendarModifiersClassNames}
              />
            </div>
          </div>
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-lg font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h2>
            {selectedDayEvents.length > 0 ? (
              <div className="space-y-3">
                {selectedDayEvents.map((event) => {
                  const type = getTypeInfo(event.event_type);
                  return (
                    <div key={event.id} className="surface-card p-4" data-testid={`event-${event.id}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 ${type.color} rounded-xl flex items-center justify-center text-white flex-shrink-0`}>
                          {type.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-sm font-semibold text-foreground">{event.title}</h3>
                              <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full mt-0.5 ${type.bg}`}>{type.label}{event.circle_name ? ` · ${event.circle_name}` : ''}</span>
                            </div>
                            <div className="flex gap-1">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-cyan-50"><Download className="w-3.5 h-3.5 text-cyan-600" /></Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => window.open(getGoogleUrl(event),'_blank')}>Google Calendar</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => window.open(getOutlookUrl(event),'_blank')}>Outlook</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => downloadICS(event)}>Apple Calendar (.ics)</DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-cyan-50" onClick={() => openEdit(event)} data-testid={`edit-event-${event.id}`}>
                                <Pencil className="w-3.5 h-3.5 text-cyan-600" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-rose-50" onClick={() => handleDeleteEvent(event.id)} data-testid={`delete-event-${event.id}`}>
                                <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                              </Button>
                            </div>
                          </div>
                          <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5" />
                              {event.all_day ? 'All day' : (
                                <span>{new Date(event.start_time).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})}{event.end_time && ` – ${new Date(event.end_time).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})}`}</span>
                              )}
                            </div>
                            {event.location && <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{event.location}</div>}
                          </div>
                          {event.description && <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{event.description}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="surface-card py-14 text-center">
                <CalendarDays className="w-12 h-12 text-emerald-200 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-1">Nothing on this day</p>
                <p className="text-xs text-muted-foreground/60">Pick a date or add something new</p>
                {circles.length > 0 && (
                  <Button size="sm" onClick={() => setAddEventDialogOpen(true)} className="btn-primary mt-4" data-testid="empty-add-event-btn">
                    <Plus className="w-4 h-4 mr-1" /> New Event
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* EDIT EVENT DIALOG */}
      {editEvent && (
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="surface-card border-0 shadow-xl max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Edit Event</DialogTitle>
              <DialogDescription>Update the details for this event</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateEvent} className="space-y-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={editEvent.event_type} onValueChange={(v) => setEditEvent({ ...editEvent, event_type: v })}>
                  <SelectTrigger data-testid="edit-event-type-select" className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{eventTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input data-testid="edit-event-title-input" value={editEvent.title} onChange={(e) => setEditEvent({ ...editEvent, title: e.target.value })} className="rounded-xl" required />
              </div>
              <div className="space-y-2">
                <Label>Details (optional)</Label>
                <Textarea data-testid="edit-event-description-input" value={editEvent.description || ''} onChange={(e) => setEditEvent({ ...editEvent, description: e.target.value })} className="resize-none rounded-xl" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Start</Label><Input type="datetime-local" data-testid="edit-start-time-input" value={editEvent.start_time} onChange={(e) => setEditEvent({ ...editEvent, start_time: e.target.value })} className="rounded-xl" required /></div>
                <div className="space-y-2"><Label>End</Label><Input type="datetime-local" data-testid="edit-end-time-input" value={editEvent.end_time || ''} onChange={(e) => setEditEvent({ ...editEvent, end_time: e.target.value })} className="rounded-xl" /></div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!editEvent.all_day} onCheckedChange={(c) => setEditEvent({ ...editEvent, all_day: c })} data-testid="edit-all-day-switch" />
                <Label>All day</Label>
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Input data-testid="edit-location-input" value={editEvent.location || ''} onChange={(e) => setEditEvent({ ...editEvent, location: e.target.value })} className="rounded-xl" />
              </div>
              <Button type="submit" className="w-full btn-primary" data-testid="save-event-btn">Save Changes</Button>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default CalendarPageStandalone;
